<template>
  <aside :class="[$style.container, { [$style.asideExpanded]: isExpanded }]">
    <section :class="$style.content">
      <header :class="$style.asideButtons">
        <ButtonBack
          v-if="showBackButton"
          :class="$style.backButton"
          @click="handleBackClick"
        />
        <ButtonClose
          v-if="showCloseButton"
          :class="$style.closeButton"
          @click="handleItemCancel"
        />
      </header>
      <div :class="$style.instructions">
        <slot name="instructions" />
      </div>
      <div :class="$style.bodyContainer">
        <div :class="$style.sidebar">
          <div :class="$style.iconContainer">
            <slot name="avatar" />
          </div>
          <HeadingMedium
            :class="$style.headerText"
            :data-test-id="$testId('headingText')"
          >
            <transition
              :enterActiveClass="$style.headerTextTransition"
              :enterClass="$style.headerTextHidden"
              :leaveActiveClass="$style.headerTextTransition"
              :leaveToClass="$style.headerTextHidden"
            >
              <span
                v-if="showLoading"
                key="loading"
                :class="$style.headerLoadingIcon"
              >
                <LoadingIcon
                  :scale="1"
                />
                Loading
              </span>
              <slot
                v-else
                name="headerText"
              >
                <span>{{ headerText }}</span>
              </slot>
            </transition>
          </HeadingMedium>
          <slot name="leftPaneCustomContent" />
          <slot name="status" />
          <slot name="asideHeaderMessages" />
        </div>
        <div :class="$style.body">
          <nav :class="$style.bodyNav">
            <ul
              ref="bodyNavItems"
              :class="[$style.bodyNavItems, collapseAtClass, overrideBodyNavItems]"
            >
              <li
                v-for="(component, index) in $props.components"
                :key="index"
                :class="[$style.bodyNavItem, getSelectedClassName(index, true)]"
                :data-walkthrough="(index === selectedContentIndex)
                  ? 'asidePanelTabSelected' : false"
              >
                <a
                  :class="$style.bodyNavItemLink"
                  data-walkthrough="asidePanelTabLink"
                  href="javascript:void(0);"
                  @click.prevent="handleBodyNavItemLinkClick(index, component.label)"
                >
                  <span :class="[$style.bodyNavItemLinkLabel, $style[component.label]]">
                    <template v-if="!!component.customLabel">
                      <component
                        :is="component.customLabel.vueComponent"
                        v-bind="component.customLabel.vueComponentProps"
                      />
                    </template>
                    <template v-else>{{ component.label }}</template>
                    <svg viewBox="0 0 26 14" xmlns="http://www.w3.org/2000/svg" v-if="component.isNew" :class="$style.newIcon" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g fill="none" fill-rule="evenodd"><rect fill="#FFB000" width="26" height="14" rx="3"/><path d="M5.34 10V6.892a4.26 4.26 0 00-.02-.424L7.964 9.84a.42.42 0 00.144.126c.049.023.11.035.185.035h.487V4.939h-.83v3.076a4.91 4.91 0 00.025.459L5.319 5.089a.73.73 0 00-.07-.078.252.252 0 00-.064-.046.246.246 0 00-.075-.02.87.87 0 00-.106-.006h-.493V10h.83zm7.8 0v-.752h-2.243V7.816h1.768v-.724h-1.768V5.688h2.244v-.749H9.949V10h3.192zm2.915 0l1.117-3.458.043-.143c.016-.052.03-.107.044-.165a3.298 3.298 0 00.088.308L18.453 10h.85l1.572-5.061h-.739a.329.329 0 00-.203.063.282.282 0 00-.108.15l-.861 3.028c-.019.072-.037.15-.055.235a9.627 9.627 0 00-.05.262 4.014 4.014 0 00-.133-.497l-.987-3.027a.299.299 0 00-.112-.156.34.34 0 00-.203-.058h-.277a.331.331 0 00-.311.213l-.994 3.028c-.026.075-.05.156-.072.243-.022.088-.043.18-.061.278a8.79 8.79 0 00-.051-.278 3.268 3.268 0 00-.058-.243l-.86-3.027a.278.278 0 00-.111-.156.348.348 0 00-.205-.058h-.79L15.203 10h.851z" fill="#000" fill-rule="nonzero"/></g></svg>
                  </span>
                </a>
              </li>
            </ul>
          </nav>
          <div
            v-for="(component, index) in $props.components"
            :key="index"
            :class="[$style.bodyContent, getSelectedClassName(index), bodyContentClassName]"
          >
            <NotificationInPage
              v-if="component.notificationConfig"
              :class="$style.notificationInPage"
              :notification="component.notificationConfig"
              :showNotification="component.showNotification"
              @close="component.notificationOnClose()"
            />
            <AsidePanelBodyContentHeader
              v-if="component.headerLabels && errorFetchingItem === false"
              :childItemType="component.itemType"
              :headerLabels="component.headerLabels"
              :parentItemType="itemType"
            />
            <div v-if="component.backboneView">
              <strong>TODO:</strong>
              &nbsp;Display the {{ String(component.label).toLowerCase() }}
              Backbone View here!
            </div>
            <component
              :is="component.vueComponent"
              v-if="component.vueComponent
                && errorFetchingItem === false
                && renderedComponentIndexes.includes(index)"
              v-bind="component.vueComponentProps"
              :itemType="component.itemType"
              :parentType="itemType"
              :viewport="viewport"
              v-on="component.vueComponentEvents"
              @enableCta="handleEnableCta"
            />
            <p
              v-if="!component.backboneView && !component.vueComponent"
              :class="$style.bodyContentPlaceholder"
            >
              <strong>TODO:</strong>
              &nbsp;Display the {{ String(component.label).toLowerCase() }}
              content here!
            </p>
          </div>
        </div>
      </div>
      <footer :class="$style.footer">
        <nav :class="$style.footerNav">
          <slot name="footerCustomContent" />
          <ul :class="$style.actions">
            <li :class="[$style.action, $style.actionCancel]">
              <a
                :class="$style.actionLink"
                href="javascript:void(0)"
                @click.prevent="handleItemCancel()"
              >{{ cancelText }}</a>
            </li>
            <li
              :class="$style.action"
            >
              <slot name="additionalActions" />
            </li>
            <li
              v-if="showDeleteButton"
              :class="[$style.action, $style.actionDelete]"
            >
              <FlatActionButton
                :action="handleDelete"
                :buttonClass="['btnDefault', 'btnAlert']"
                :disabled="errorFetchingItem"
                :text="deleteText"
              />
            </li>
            <li
              v-if="showSaveButton"
              :class="[$style.action, $style.actionSave]"
              data-walkthrough="aside-panel-save-button"
            >
              <GuideMessage
                caretPosition="bottom-right"
                :class="$style.saveGuide"
                name="asideSave"
              />
              <FlatActionButton
                :action="handleCTAButton"
                :buttonClass="['btnDefault', 'btnCTA']"
                :disabled="shouldDisableCta"
                :text="saveText"
              />
            </li>
          </ul>
        </nav>
      </footer>
    </section>
    <ProgressModal
      ref="progressModal"
      storeModule="ProgressModule"
    />
  </aside>
</template>

<script>
import {
  ButtonBack,
  ButtonClose,
  HeadingMedium,
  LoadingIcon,
  NotificationInPage,
} from '@jumpcloud/ui-components';
import {
  ItemActionConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  ItemTypeLabels,
  ItemTypeRoutes,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { eventProperties, itemTypePages } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { extend, get, values } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import AsidePanelBodyContentHeader from 'client/components/AsidePanel/AsidePanelBodyContentHeader.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionButton from 'client/components/Buttons/FlatActionButton.vue';
import GuideMessage from 'client/components/GuideMessage/GuideMessage.vue';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import ProgressModal from 'client/components/Modal/ProgressModal.vue';
import RouterService from 'client/util/RouterService';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import Vue from 'vue';
import onlyPosixErrors from 'client/util/UserGroupAsideUtil';

const {
  description,
  location,
  section,
  text,
} = eventProperties;

const {
  applicationTemplate,
  commandTemplateWindows,
  commandTemplateMacOs,
  commandTemplateLinux,
  ldap,
  ssoTemplate,
  systemUser,
} = ItemTypeConsts;
const commandTemplates = [commandTemplateWindows, commandTemplateMacOs, commandTemplateLinux];
const { create, update } = ItemActionConsts;

export default {
  name: 'AsidePanel',

  components: {
    AsidePanelBodyContentHeader,
    ButtonBack,
    ButtonClose,
    FlatActionButton,
    GuideMessage,
    HeadingMedium,
    LoadingIcon,
    NotificationInPage,
    ProgressModal,
  },

  inject: ['$validator'],

  mixins: [AsidePanelPropsMixin],

  props: {
    bodyContentClassName: {
      type: String,
      default: '',
    },
    cancelText: {
      type: String,
      default: 'cancel',
    },
    components: {
      type: Array,
      required: true,
      validator(components) {
        return components.every((component) => {
          const {
            label,
            customLabel,
            vueComponent,
            vueComponentProps,
            headerLabels,
            itemType,
            notificationConfig,
            renderImmediately,
            showNotification,
          } = component;

          if ((typeof label !== 'string' || label.length === 0)
          && (typeof customLabel !== 'object' || typeof customLabel.vueComponent !== 'object')) {
            return false;
          }

          if (vueComponent !== undefined) {
            const type = typeof vueComponent;

            if (type !== 'string' && type !== 'object') {
              return false;
            }
          }

          if (vueComponentProps !== undefined && typeof vueComponentProps !== 'object') {
            return false;
          }

          if (headerLabels !== undefined && (!headerLabels[create] || !headerLabels[update])) {
            return false;
          }

          if (itemType !== undefined && !values(ItemTypeConsts).includes(itemType)) {
            return false;
          }

          if (showNotification !== undefined && typeof showNotification !== 'boolean') {
            return false;
          }

          if (renderImmediately !== undefined && typeof renderImmediately !== 'boolean') {
            return false;
          }

          if (notificationConfig !== undefined && typeof notificationConfig !== 'object') {
            return false;
          }

          return true;
        });
      },
    },
    ctaNeedsChange: {
      type: Boolean,
      default: false,
    },
    deleteText: {
      type: String,
      default: 'delete',
    },
    disableCta: {
      type: Boolean,
      default: false,
    },
    eventBus: {
      type: Object,
      required: false,
      default: () => new Vue(),
    },
    handleBackClick: {
      type: Function,
      default: () => {},
    },
    handleCancel: {
      type: Function,
      default: () => {},
    },
    handleCtaBtnChange: {
      type: Function,
      default: () => {},
    },
    handleDelete: {
      type: Function,
      default: () => {},
    },
    handleMountedCallback: {
      type: Function,
      default: () => {},
    },
    handleSave: {
      type: Function,
      default() {
        return Promise.resolve().then(() => this.redirect());
      },
    },
    handleSelectEvent: {
      type: Function,
      default: () => {},
    },
    initialTabIndex: {
      type: Number,
      default: 0,
    },
    navigateToTab: {
      type: Object,
      default: () => {},
      validator(obj) {
        if (obj) {
          return typeof obj.tabLabel !== 'undefined';
        }
        return true;
      },
    },
    noNotification: {
      type: Boolean,
      default: false,
    },
    noRedirect: {
      type: Boolean,
      default: false,
    },
    overrideBodyNavItems: {
      type: [Object, String],
      default: '',
    },
    // Allow parent to specify route to redirect to if necessary.
    redirectTo: {
      type: String,
      default: '',
    },
    saveText: {
      type: String,
      default: 'save',
    },
    showBackButton: Boolean,
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    showDeleteButton: {
      type: Boolean,
      default: false,
    },
    showSaveButton: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      ctaButtonChildEnabled: false,
      ctaButtonChanged: false,
      collapseAtWidth: undefined,
      errorFetchingItem: undefined,
      loading: false,
      renderedComponentIndexes: [],
      selectedContentIndex: 0,
      storeModel: undefined,
    };
  },

  computed: {
    canceledEventName() {
      return this.getEventName('canceled');
    },

    collapseAtClass() {
      return typeof this.collapseAtWidth === 'number'
        ? this.$style[`bodyNavItemsCollapseAt${this.collapseAtWidth}`]
        : '';
    },

    /**
     * @todo
     * 1. Ensure the getDisplayName getter is implemented in all Vuex Model
     *    modules via a mixin or base class.
     */
    headerText() {
      if (this.errorFetchingItem) {
        return '';
      }

      if (this.itemType === applicationTemplate || this.itemType === ssoTemplate) {
        return 'Configure New SSO Application';
      }

      if (commandTemplates.includes(this.itemType)) {
        return 'Configure New Command from Template';
      }

      if (this.isItemBeingCreated) {
        return `New ${ItemTypeDisplayNames[this.itemType]}`;
      }

      return this.displayName;
    },

    invalidFields() {
      return !onlyPosixErrors(this.v2Errors.items);
    },

    renderedEventName() {
      return this.getEventName('render');
    },

    route() {
      if (this.itemType === ldap) {
        // The LDAP Aside Panel can be displayed on either the LDAP or Directories page. So use a
        // regex to return to the page that it is displayed on.
        const hash = PageNavigationUtil.getLocationHash();
        return hash.replace(/\/ldap_server\/.+\/details/, '');
      }

      return this.redirectTo || ItemTypeRoutes[this.itemType] || ItemTypeRoutes.default;
    },

    savedEventName() {
      return this.getEventName('saved');
    },

    shouldDisableCta() {
      if (this.invalidFields) {
        return true;
      }
      return (this.disableCta && !this.ctaButtonChildEnabled) || this.errorFetchingItem;
    },

    showBanner() {
      return !this.isItemBeingUpdated && !this.itemId && this.itemType === systemUser;
    },

    showLoading() {
      let showLoading = true;

      if (this.errorFetchingItem) {
        showLoading = false;
      } else {
        showLoading = (this.isItemBeingUpdated && !this.displayName) || this.loading;
      }

      return showLoading;
    },
  },

  watch: {
    navigateToTab: {
      handler(newValue) {
        if (newValue) {
          const componentIndex = this.components.findIndex(
            (item) => {
              const label = get(item, 'label') || get(item, 'customLabel.vueComponentProps.label');
              if (newValue.tabLabel && label) {
                return newValue.tabLabel.toLowerCase().replace(' ', '') === label.toLowerCase().replace(' ', '');
              }
              return false;
            },
          );
          if (componentIndex < 0) return;
          if ((componentIndex !== this.selectedContentIndex)) {
            this.handleBodyNavItemLinkClick(componentIndex, newValue.tabLabel);
          }
        }
      },
      immediate: true,
    },
  },

  /**
   * Extend the computed properties and methods here, before the component is
   * created so that the namespace argument to mapActions() and mapGetters()
   * can be set dynamically from the props passed to the AsidePanel component.
   * @see https://github.com/vuejs/vuex/issues/863#issuecomment-315619012
   */
  beforeCreate() {
    const storeModel = StoreModelMap[this.$options.propsData.itemType];
    const asidePanelEventsModule = StoreModuleMap[ItemTypeConsts.asidePanelEvents];

    if (!storeModel) {
      return; // prevent execution from vuex code if there's no storeModel
    }

    extend(
      this.$options.computed,
      mapGetters(storeModel, [
        'displayName',
        'idField',
        'isItemBeingCreated',
        'isItemBeingUpdated',
        'item',
      ]),
    );

    extend(
      this.$options.methods,
      mapActions(storeModel, [
        'fetchItem',
        'resetItem',
        'saveItem',
        'updateItem',
        'setItemStateToCreate',
        'setItemStateToUpdate',
      ]),
      mapActions(asidePanelEventsModule, [
        'setAsidePanelError',
      ]),
      mapActions(notificationModule, [
        'triggerNotification',
        'triggerErrorNotification',
        'triggerSuccessNotification',
      ]),
    );
  },

  /**
   * The created lifecycle hook is used to set the storeModel on the component instance because
   * data is not yet available in the beforeCreate hook.
   */
  created() {
    this.storeModel = StoreModelMap[this.$options.propsData.itemType];
  },

  mounted() {
    /**
     * @todo This "setTimeout" is a temporary solution to solve a problem related to the transition
     * effect when the aside panel gets opened or closed, the idea is to remove it when all
     * the legacy routes are moved to the Vue Router.
     */
    window.setTimeout(() => this.handleMounted(), 500);

    this.eventBus.$on('mainActionSaveButtonClick', () => {
      this.$nextTick(() => this.handleCTAButton());
    });
  },

  beforeDestroy() {
    this.handleBeforeDestroy();
  },

  methods: {
    /**
     * Determine the minimum width of the body navigation items below which
     * they must be collapsed. When collapsed the labels will be hidden for
     * all tabs except the selected tab. The width set here will be used in
     * class name trigged by a media query below. This differs from the
     * original solution used in the TabBar Backbone view which used a CSS
     * Element Queries polyfill for media queries applied to elements rather
     * than the viewport.
     */
    calculateCollapseAtWidth() {
      const { bodyNavItems } = this.$refs;
      if (bodyNavItems) {
        const bodyNavItemsWidth = bodyNavItems.getBoundingClientRect()
          .width;
        this.collapseAtWidth = Math.ceil(bodyNavItemsWidth / 50) * 50;
      }
    },

    /**
     * @todo Events are currently used to inform the main panel component when
     * the aside panel component changes state. This can be refactored when
     * Vue Router is used.
     */
    getEventName(eventType) {
      let eventName = eventType;

      if (this.itemType === systemUser) {
        eventName = `user-details:${eventName}`;
      }

      return eventName;
    },

    getSelectedClassName(index, isNavItem = false) {
      if (this.selectedContentIndex === index) {
        return isNavItem
          ? this.$style.bodyNavItemSelected
          : this.$style.bodyContentSelected;
      }
      return '';
    },

    handleBeforeDestroy() {
      if (this.storeModel) {
        this.resetItem();
      }
    },

    handleBodyNavItemLinkClick(index, label) {
      const { bodyNavItems } = this.$refs;
      if (bodyNavItems && bodyNavItems.childNodes[index]) {
        const tab = this.$refs.bodyNavItems.childNodes[index];
        tab.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }

      this.handleSelectEvent(label);

      EventHelper.triggerTabClicked({
        [description]: `Navigate ${ItemTypeDisplayNames[this.itemType]} tabs`,
        [location]: itemTypePages[this.itemType],
        [text]: label,
      });

      this.renderComponentsIndex(index);
      this.selectedContentIndex = index;
      this.$emit('cta-change', this.selectedContentIndex, false);
      this.$emit('nav-change', label);
    },

    async handleCTAButton() {
      const newIndex = this.selectedContentIndex + 1;
      await this.handleItemSave();
      this.$emit('cta-change', newIndex, true);

      EventHelper.triggerButtonClicked({
        [description]: `Saves an individual ${ItemTypeLabels[this.itemType]}`,
        [location]: `${itemTypePages[this.itemType]} Aside`,
        [section]: 'bottom',
        [text]: this.saveText,
      });
    },

    handleEnableCta() {
      this.ctaButtonChildEnabled = true;
    },

    handleItemCancel() {
      EventHelper.triggerLinkClicked({
        [description]: `Cancels an individual ${ItemTypeLabels[this.itemType]} changes`,
        [location]: `${itemTypePages[this.itemType]} Aside`,
        [section]: 'bottom',
        [text]: this.cancelText,
      });

      RouterService.redirect({
        path: this.route,
        triggeredEvent: this.canceledEventName,
        viewport: this.viewport,
        vueRouter: this.$router,
      });

      this.handleCancel();
    },

    handleItemSave() {
      if (!this.storeModel) return Promise.resolve();

      this.loading = true;

      return this
        .handleSave()
        .then((panelResult = new SaveReturnValue()) => {
          // TODO: Consider removing the noRedirect props and instead respecting
          // the corresponding properties of the SaveReturnValue instance.
          if (!panelResult.noNotification && !this.noNotification) {
            if (panelResult.message) {
              const { type, message } = panelResult.message;
              this.triggerNotification({ type, message });
            } else {
              // DO NOT REMOVE, this notifcation used on the aside panel's
              // child components O365, GSuite etc.
              this.triggerSuccessNotification({
                message: `${ItemTypeDisplayNames[this.itemType]} saved successfully!`,
              });
            }
          }

          if (panelResult.canContinue === false) {
            // Remain on the aside panel if the parent's handleSave() method resolved with the
            // value false. Note that an object is returned here, with a default value of true for
            // canContinue. This is done to allow additional status to be returned from the parent
            // as well as ensure this new functionality is backwards compatible.

            return;
          }

          if (!this.noRedirect) this.redirect();
        })
        .catch((error) => {
          let message = `**Unable to save ${ItemTypeDisplayNames[this.itemType]}!**`;

          if (error && error.details && error.details.length > 0) {
            message = error.details[0].fieldViolationsList[0].field.indexOf('radius') > -1
              ? 'There was an issue validating RADIUS Reply Attributes on the RADIUS tab' : message;

            this.setAsidePanelError(error.details);
          }

          if (error && error.message && !error.details) {
            message = `${message}\n${error.message}`;
          }

          this.triggerErrorNotification({ isMarkdown: true, message });
          return error;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    handleMounted() {
      if (this.viewport) {
        this.viewport.trigger(this.renderedEventName);
      }

      UiEvents.triggerAsideViewed({
        description: `Views ${ItemTypeDisplayNames[this.itemType]} aside`,
        page: itemTypePages[this.itemType] || this.$page,
      });

      this.calculateCollapseAtWidth();

      // Populate the renderedComponentIndexes data member with the initial selected content index
      // and the indexes of the components that have the renderImmediately property set to true.
      if (!this.navigateToTab) {
        this.selectedContentIndex = this.initialTabIndex;
      }
      this.renderedComponentIndexes.push(this.selectedContentIndex);
      this.renderedComponentIndexes.push(...this.components.reduce((acc, cur, idx) => {
        if (cur.renderImmediately) {
          acc.push(idx);
        }

        return acc;
      }, []));

      if (!this.storeModel) {
        this.errorFetchingItem = false;
        this.handleMountedCallback();
        return;
      }

      if (this.itemId) {
        this.setItemStateToUpdate();
        this.updateItem({ [this.idField]: this.itemId });
      } else {
        this.setItemStateToCreate();
      }

      this.fetchItem()
        .then(() => {
          this.errorFetchingItem = false;
          this.handleMountedCallback();
        })
        .catch(() => {
          // Reset item ID so that upon closing the aside the item _hasn't_ changed.
          this.updateItem({ [this.idField]: '' });
          this.errorFetchingItem = true;
          this.triggerErrorNotification({
            message: `Unable to retrieve ${ItemTypeDisplayNames[this.itemType].toLowerCase()} data. Please refresh the page and try again.`,
          });
        });
    },

    handleRedirect() {
      RouterService.redirect({
        path: this.route,
        triggeredEvent: this.savedEventName,
        viewport: this.viewport,
        vueRouter: this.$router,
      });
    },

    /**
     * If the ProgressModal is visible then wait for it to be closed before
     * redirecting to the previous page. This is necessary because once the
     * redirect is triggered the AsidePanel will be removed and if the ProgressModal
     * hasn't been closed it will remain open because the Vue instance it was
     * mounted in will have been destroyed.
     */
    redirect() {
      if (this.$refs.progressModal.visible) {
        this.$refs.progressModal.$once('closed', () => this.handleRedirect());
      } else {
        this.handleRedirect();
      }
    },

    renderComponentsIndex(index) {
      if (!this.renderedComponentIndexes.includes(index)) {
        this.renderedComponentIndexes.push(index);
      }
    },
  },
};
</script>

<style module>
@value (max-width-small, max-width-medium, max-width-x-large) from '~css/breakpoints.css';
@value (headerHeight) from '~css/layout.css';
@value (panelSpeed, panelTimingFunction) from '~css/transitions.css';
@value (asideShadow) from '~css/shadows.css';

.container {
  --asideSpacing: 20px;

  background-color: var(--jcWhite);
  bottom: 0;
  box-shadow: asideShadow;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 0;
  transition-duration: panelSpeed;
  transition-property: bottom, height, left, right, top, width, transform;
  transition-timing-function: panelTimingFunction;
  width: var(--asideWidth, 0);
  z-index: 1000;
}

:global(.aside-expanded) .container,
.container.asideExpanded {
  --asideWidth: calc(100% - 12rem);

  overflow-x: hidden;
  overflow-y: auto;
  transition-duration: panelSpeed;
  transition-property: transform;
  transition-timing-function: panelTimingFunction;
}

.content {
  display: grid;
  grid-template:
    'asideButtons asideButtons' auto
    'asideInstructions asideInstructions' auto
    'asideBody asideBody' 1fr
    'asideFooter asideFooter' auto / 1fr auto;
  height: 100%;
  position: relative;
  width: 100%;
}

.instructions {
  grid-area: asideInstructions;
}

.asideButtons {
  display: grid;
  grid-area: asideButtons;
  grid-template: 'asideBackButton asideCloseButton' auto / auto auto;
  justify-content: space-between;
  padding: var(--asideSpacing);
}

.backButton {
  grid-area: asideBackButton;
}

.closeButton {
  grid-area: asideCloseButton;
}

.bodyContainer,
.sidebar {
  min-height: 0;
  min-width: 0;
  overflow-x: visible;
  overflow-y: auto;
}

.bodyContainer {
  display: grid;
  grid-area: asideBody;
  grid-template-columns: var(--asideSidebarWidth, 300px) 1fr;
  width: 100%;
}

.sidebar {
  height: 100%;
  padding: var(--asideSpacing);
}

.iconContainer {
  margin: 0 auto var(--asideSpacing);
}

.headerLoadingIcon {
  column-gap: 0.5rem;
  display: flex;
  justify-content: center;
}

.headerText {
  opacity: 1;
  overflow: hidden;
  position: relative;
  text-align: center;
  text-overflow: var(--headerTextTextOverflow, clip);
  white-space: var(--headerTextWhiteSpace, normal);
  word-wrap: break-word;
}

.headerTextHidden {
  opacity: 0;
}

.headerTextTransition {
  transition: opacity panelSpeed panelTimingFunction;
}

.newIcon {
  width: 26px;
}

.body {
  display: grid;
  grid-auto-rows: auto 1fr;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow-x: hidden;
}

.bodyNav {
  margin-bottom: 2px;
  min-height: 0;
  min-width: 0;
  padding: 4px var(--asideSpacing);
  white-space: nowrap;
}

.bodyNavItems {
  display: inline-block;
  height: 100%;
  list-style: none;
  margin: 0;
  overflow-x: scroll;
  padding: 0 0 10px 0;
  width: 100%;
}

.bodyNavItems::-webkit-scrollbar {
  height: 3px;
  width: 1px;
}

.bodyNavItems::-webkit-scrollbar-track {
  background: var(--jcWhite);
}

.bodyNavItems::-webkit-scrollbar-thumb {
  background: var(--jcGrey300);
}

.bodyNavItem {
  border-bottom: 3px solid transparent;
  color: var(--jc-text-color-light);
  display: inline-block;
  margin-right: 30px;
}

.saveGuide {
  right: 200px;
  top: -175px;
}

.bodyNavItemSelected {
  border-bottom: 3px solid var(--jcPrimary);
  color: var(--jc-text-color-dark);
}

.bodyNavItemLink {
  color: currentColor;
  display: inline-block;
  padding: 1px 0;
}

.bodyNavItemLink:focus,
.bodyNavItemLink:hover {
  text-decoration: none;
}

.bodyNavItemLinkLabel {
  display: inline-block;
  font-size: 1.3rem;
  font-weight: 700;
}

.bodyNavItemLinkLabel:hover {
  color: var(--jc-text-color-dark);
}

.bodyNavItemLinkLabel:not(.iOS) {
  text-transform: capitalize;
}

/**
 * Collapse the label in the bodyNavItems when the viewport is below a specific
 * width. The *CollapseAt* classes are added above by JavaScript based on the
 * actual width of the bodyNavItems. The max-width of the viewport is determined
 * from the following forumula which is based on the fact that the aside element
 * is _always_ 60% of the width of the viewport:
 *
 * Collapse At Width = 0.60 * Viewport Max Width
 * Viewport Max Width = Collapse At Width / 0.60
 *
 * Rather than use CSS element queries from a polyfill as done for the TabBar
 * Backbone view, a few cases are simply handled here. Please add additional
 * cases if the tabs rendered in an AsidePanel don't fit those listed below.
 *
 * @see http://marcj.github.io/css-element-queries/
 * @see https://github.com/TheJumpCloud/jumpcloud-admin-portal/tree/master/client/components/TabBar
 */
@media screen and (max-width: 555px) {
  .bodyNavItemsCollapseAt300 .bodyNavItemLinkLabel {
    display: none;
  }

  .bodyNavItemsCollapseAt300 .bodyNavItemSelected .bodyNavItemLinkLabel {
    display: inline-block;
  }
}

@media screen and (max-width: 740px) {
  .bodyNavItemsCollapseAt400 .bodyNavItemLinkLabel {
    display: none;
  }

  .bodyNavItemsCollapseAt400 .bodyNavItemSelected .bodyNavItemLinkLabel {
    display: inline-block;
  }
}

@media screen and (max-width: 1018px) {
  .bodyNavItemsCollapseAt550 .bodyNavItemLinkLabel {
    display: none;
  }

  .bodyNavItemsCollapseAt550 .bodyNavItemSelected .bodyNavItemLinkLabel {
    display: inline-block;
  }
}

@media screen and (max-width: 1481px) {
  .bodyNavItemsCollapseAt800 .bodyNavItemLinkLabel {
    display: none;
  }

  .bodyNavItemsCollapseAt800 .bodyNavItemSelected .bodyNavItemLinkLabel {
    display: inline-block;
  }
}

.bodyContent {
  display: none;
  min-height: 0;
  min-width: 0;
}

.bodyContentSelected {
  display: flex;
  flex-direction: column;
}

.bodyContentPlaceholder {
  margin: 2vh 1vw;
}

.footer {
  background-color: var(--jc-background-light);
  border-top: var(--jc-border);
  grid-area: asideFooter;
  padding: 15px 10px;
  text-align: right;
  width: 100%;
}

.footerNav {
  align-items: center;
  display: flex;
  justify-content: flex-end;
}

.actions {
  display: inline-block;
  list-style: none;
  margin: 0;
}

.action {
  display: inline-block;
}

.actionCancel,
.actionDelete {
  margin-right: var(--asideSpacing);
}

.actionLink {
  color: var(--jc-text-color);
  font-size: 12px;
  font-weight: 400;
  text-decoration: underline;
}

.actionLink:focus,
.actionLink:hover {
  color: var(--jc-text-color-dark);
}

.actionSave {
  margin-right: var(--asideSpacing);
}

.notificationInPage {
  margin: 0 auto 1rem;
  max-width: calc(100% - 40px);
  width: 100%;
}

@media (max-width: max-width-x-large) {
  .container {
    --asideSidebarWidth: 250px;
  }

  :global(.aside-expanded) .container,
  .container.asideExpanded {
    --asideWidth: calc(100% - 6rem);
  }
}

@media (max-width: max-width-medium) {
  :global(.aside-expanded) .container,
  .container.asideExpanded {
    --asideWidth: 100%;
  }

  .bodyContainer {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }

  .iconContainer {
    display: none;
  }

  .body {
    padding-top: 0;
  }

  .asideButtons {
    padding-bottom: 0;
  }
}

@media (max-width: max-width-small) {
  .notificationInPage {
    max-width: calc(100% - 50px);
  }
}
</style>
